import React, { memo } from 'react';
import dynamic from 'next/dynamic';
import { withBonnetPage } from '@bonnet/next/page';
import _get from 'lodash/get';
import { pageNames } from '@atc/bonnet-paths';
import { ClientOnly } from '@atc/react-client-only';
import { useFeatures } from 'reaxl-features';
import { fetchCompositeJSON } from '@bonnet/next/fetch';
import { withCtxMiddleware } from '@bonnet/next/ctx-middleware';
import { withPresetCollections } from '@/context-middleware';

import { VehicleSearchProvider } from '@/contexts/VehicleSearchContext';

import {
    AdsProviderContainer,
    PageHeadContainer,
    RichSchemaContainer,
    NewsReviewsContainer,
    AdditionalServicesContainer,
    SeoDeepLinksContainer,
    DealerLotSingleContainer,
    BrowseByStylesContainer,
    GlobalNavContainer,
    RefreshedHero,
    TariffAdBarContainer,
} from '@/containers';

import {
    SuperHeroCarousel,
    HomePageAdsContainer,
    RecentSearchesContainer,
    MarketingValueContainer,
} from '@/containers/hp';

import BrowseByBudgetContainer from '@/containers/BrowseByBudgetContainer';

import {
    BrowseByBudgetPlaceholder,
    ELotPlaceholder,
    LazyComponent,
} from '@/component';

import {
    fetchMakes,
    fetchArticles,
    fetchDeepLinks,
    fetchMarketingValue,
    fetchEnhancedSuperHero,
} from '@/fetchers';

import { getMetaTags, getPageAnalytics, formatEnhancedHeroData } from '@/utilities';
import { landingPageValues } from '@/constants';

import {
    userDuck,
    inventoryDuck,
    ownersDuck,
    homeServicesDuck,
    dealerLotDuck,
    pageDetailsDuck,
} from '@/ducks';

import { PersonalizationEngineModule, AuthModule } from '@/modules';
import { getContent, homepageContent, styleCodes } from '@/reference';
import getOptimizelyEdgeUrl from '@/utilities/getOptimizelyEdgeUrl';
import useSetPageAnalytics from '@/hooks/useSetPageAnalytics';

const GhostAdsContainer = dynamic(() => import('../containers/GhostAdsContainer'), { ssr: false });
const SmartBannerContainer = dynamic(() => import('../containers/SmartBannerContainer'), { ssr: false });

const HomePage = memo((props) => {
    const { ad,
        articles,
        superHeroData,
        meta,
        heading,
        pageData,
        pageInst,
        userZipCode,
        vehicleMakes,
        isBot,
        marketingValueData,
        mockQueries,
        footerLinks,
    } = props;
    const {
        pageName,
        pageType,
        pageValue,
        querySearch,
    } = pageData;
    const heroSlotId = 'j_id_1m8';

    const { iscypress = 'false' } = querySearch;
    const byPassBot = iscypress && iscypress === 'true' || !isBot;

    const { HOMEPAGE } = pageNames;

    useSetPageAnalytics({
        pageInst,
        pageName: HOMEPAGE,
        inputValue: pageName,
        querySearch,
        pageType: 'home',

    });

    // new search box layout and header variants
    const {
        refreshed_hero_test: [isRefreshedHeroEnabled, { enableRefreshedHeroVariantA, enableRefreshedHeroVariantB }],
        hp_urgent_cta_bar: [isTariffAdBarEnabled],
    } = useFeatures(['refreshed_hero_test', 'hp_urgent_cta_bar']);

    return (
        <VehicleSearchProvider data={{ userZipCode, vehicleMakes }}>
            <SmartBannerContainer />

            <GlobalNavContainer className="transparent-header" />
            {/* This div is needed to allow the z-index arrangement of searchBadge links and popover menu to work with a gradrient bg*/}
            <div className="transparentHeaderGradientBackground" />

            <RichSchemaContainer
                meta={meta}
            />
            <PageHeadContainer
                meta={meta}
                pageType={pageType}
            />

            <AdsProviderContainer
                adConfig={ad}
                pageInst={pageInst}
            >
                {isRefreshedHeroEnabled ? (
                    <RefreshedHero
                        isHomepage
                        headingOneTitle={meta.h1 || heading.title}
                        styleCodes={styleCodes}
                        userZipCode={userZipCode}
                        heroSlotId={heroSlotId}
                        originPage="home"
                        isRefreshedHeroEnabled={isRefreshedHeroEnabled}
                        enableRefreshedHeroVariantA={enableRefreshedHeroVariantA}
                        enableRefreshedHeroVariantB={enableRefreshedHeroVariantB}
                    />
                ) : (
                    <SuperHeroCarousel
                        isHomepage
                        headingOneTitle={meta.h1 || heading.title}
                        superHeroData={superHeroData}
                        styleCodes={styleCodes}
                        userZipCode={userZipCode}
                        heroSlotId={heroSlotId}
                        originPage="home"
                    />
                )}

                {isTariffAdBarEnabled && <TariffAdBarContainer />}

                <ClientOnly>
                    {byPassBot && (
                        <RecentSearchesContainer />
                    )}
                </ClientOnly>

                <ELotPlaceholder userZipCode={userZipCode}>
                    <LazyComponent>
                        {byPassBot && userZipCode && (
                            <DealerLotSingleContainer
                                isHomepage
                                pageName={pageName}
                                searchZip={userZipCode}
                                mockQueries={mockQueries}
                            />
                        )}
                    </LazyComponent>
                </ELotPlaceholder>

                <BrowseByBudgetPlaceholder>
                    <ClientOnly>
                        <BrowseByBudgetContainer withPixallData />
                    </ClientOnly>
                </BrowseByBudgetPlaceholder>

                {marketingValueData?.body_text?.length > 0 && marketingValueData.cta_text && marketingValueData.destination_url && (
                    <MarketingValueContainer marketingValueData={marketingValueData} />
                )}

                {ad && <HomePageAdsContainer />}

                <BrowseByStylesContainer
                    pageValue={pageValue}
                    styleCodes={styleCodes}
                    zipCode={userZipCode}
                />

                <AdditionalServicesContainer />

                <NewsReviewsContainer
                    articles={articles}
                    pageType={pageType}
                    pageValue={pageValue}
                    heading="See Car"
                    headingClassName="text-primary"
                    showCTA={false}
                />

                <ClientOnly>
                    <div
                        id="integratedAds"
                        className="hidden integrated-ads"
                    >
                        <GhostAdsContainer
                            slotId={heroSlotId}
                            pos="g"
                            adSizes={[[2, 2]]}
                        />
                    </div>
                </ClientOnly>
            </AdsProviderContainer>

            <SeoDeepLinksContainer deepLinks={footerLinks} />
        </VehicleSearchProvider>
    );
});

HomePage.displayName = 'homepage';

HomePage.getInitialProps = async (ctx) => {
    const { store, query, batchDispatch } = ctx;
    const mockQueries = query.mocks || '';

    const pageData = {
        querySearch: { ...query },
        pageType: 'HOMEPAGE', // this is different than GA pageType
        pageValue: 'homepage',
        pageName: 'atc_h',
    };

    const value = pageData.pageName?.toLowerCase();

    const pageDetails = {
        pageAnalytics: getPageAnalytics({ pageValues: landingPageValues, value }),
        siteSection: 'homepage',
        detailPageName: 'homepage',
        pageType: 'homepage',
    };

    await withCtxMiddleware([
        withPresetCollections({
            // this is used inside Google sendPageDataEvent in reaxl-analytics
            // in there, dataLayer.page.pageName 'hp' matches the switch statement to populate 'homepage' values into dataLayer.common.page
            pageName: 'hp',
            // so, if it's needed, we do need to pass in pageDetails here to make 'homepage' available in the intial dataLayer.page object
            pageDetails,
            adDetails: {
                dyn: pageData.querySearch.dyn ? pageData.querySearch.dyn : 'nl',
                type: pageData.pageType,
            },
        }),
    ], ctx);

    const pageInst = _get(ctx, ['data', 'pageInst'], undefined);
    const state = store.getState();

    const userZipCode = userDuck.selectors.getLocation(state).zip;

    batchDispatch.createActions(() => [
        // recentSearchDuck.creators.hydrateRecentlySearchedWithListings({ carouselCount: 1 }),
        userDuck.creators.setUserProfile({ currentPage: pageData.pageType }),
        pageDetailsDuck.creators.setKeys({ pixallPageInstanceId: pageInst }),
    ]);
    await batchDispatch.dispatchActions();

    const {
        articles = [],
        vehicleMakes = [],
        deepLinks,
        enhancedSuperHero,
        meta,
    } = await fetchCompositeJSON({
        articles: fetchArticles({
            key: 'data/Homepage-articles.json',
        }, mockQueries),
        enhancedSuperHero: fetchEnhancedSuperHero({}, mockQueries),
        vehicleMakes: fetchMakes({
            listingType: pageData.pageValue,
            zipCode: userZipCode,
        }, mockQueries),
        deepLinks: fetchDeepLinks({
            homePage: true,
        }, mockQueries),
        meta: getMetaTags({
            id: 'homepage',
            fallback: getContent(homepageContent, 'meta'),
            mockQueries,
        }),
    });

    const {
        heading,
        fallbackEnhancedSuperHero,
        marketValueFallBackContent,
    } = getContent(homepageContent, null, null);

    meta.optimizelyUrl = getOptimizelyEdgeUrl(meta.canonicalUrl, query, pageData.pageName);

    let superHeroData = enhancedSuperHero && Object.keys(enhancedSuperHero).length > 0 ? formatEnhancedHeroData(enhancedSuperHero) : fallbackEnhancedSuperHero;

    // add query search for uat/qa purposes
    if (pageData.querySearch.superHeroFallback) {
        superHeroData = fallbackEnhancedSuperHero;
    }

    const marketValueContent = pageData.querySearch.marketValueFallback === 'true' ? marketValueFallBackContent : await fetchMarketingValue({});

    const marketingValueData = _get(marketValueContent, '[0].acf', null);

    return {
        articles,
        superHeroData,
        meta,
        heading,
        pageData,
        pageInst,
        userZipCode,
        vehicleMakes,
        isBot: ctx.data.isBot,
        footerLinks: deepLinks,
        ad: ctx.data.ads,
        marketingValueData,
        mockQueries,
    };
};

const { BonnetPageComponent } = withBonnetPage(HomePage, {
    reducers: [
        inventoryDuck,
        ownersDuck,
        homeServicesDuck,
        userDuck,
        dealerLotDuck,
        pageDetailsDuck,
    ],
    modules: [
        AuthModule,
        PersonalizationEngineModule,
    ],
});

export default BonnetPageComponent;
